/**
 * balance-loader.ts
 * Fetches the 4 combat balance JSON files from public/data/balance/.
 * On fetch or parse failure, falls back to hardcoded defaults so the game
 * never white-screens due to a missing or malformed balance file.
 */

import type {
  BalanceConfig,
  BossesConfig,
  EnemiesConfig,
  SpawningConfig,
  PlayerConfig,
  WeaponEvolutionConfig,
} from './balance-types';

// ── Hardcoded fallbacks (mirrors the JSON files exactly) ──────────────────────

// boss-behavior: size ×~1.45 (魔王更大壓迫感) + attackA/B cooldown 略縮(彈幕更頻繁).
// 注意 applyBossBalance() 會用這裡的 size 覆蓋 pool def 的 size,故必與 pool 同步放大.
// boss-tougher (boss 2026-06-13「魔王要夠強,太弱沒挑戰性」但仍要可躲): 本表是
// bosses.json 的 fetch-失敗 fallback,必與 public/data/balance/bosses.json 完全鏡像
// (該檔才是執行期權威 source)。各 tier HP 往上(更肉)、cooldown 縮短(更兇),
// 梯度 tier1 < tier2 < tier3。可躲鐵則:只動 hp/cooldown/phase2 門檻,招式種類/子彈速度/
// 雷射 warnMs/扇彈環彈安全縫一律不動(那才是躲得掉的關鍵)。
const DEFAULT_BOSSES: BossesConfig = {
  version: 1,
  bosses: [
    // tier1-ttk (boss 2026-06-14): HP 520/600/470→820/900/740(phase2 ≈ HP/2)。舊 6–7s 秒殺 →
    // 二階段/瞬移/衝擊波沒演就死。拉到 ~9–11s 讓招式演得出、升級有感。見 docs/spec/DIFFICULTY_PROGRESSION_MODEL.md。
    // weapon-dps-pass2 (boss 2026-06-14 第二輪「王都被秒殺」): 加傷害 cap 後 median DPS 收斂,
    // tier1 HP 820/900/740→780/860/720(phase2 ≈ HP/2),中庸 TTK 仍 [9,12]s、strong ~5s。
    // 必與 public/data/balance/bosses.json 鏡像。tier2/tier3 不動(§5)。見 §7。
    { id: 'shu-mo',   hp: 780,  speedPxPerSec: 40, size: 140, attackACooldownMs: 1000, attackBCooldownMs: 2400, phase2HpThreshold: 390,  attackBWarningMs: 800 },
    { id: 'fu-hun',   hp: 860,  speedPxPerSec: 30, size: 144, attackACooldownMs: 1100, attackBCooldownMs: 3400, phase2HpThreshold: 430,  attackBWarningMs: 800 },
    { id: 'yuan-su',  hp: 720,  speedPxPerSec: 50, size: 138, attackACooldownMs: 2300, attackBCooldownMs: 2600, phase2HpThreshold: 360,  attackBWarningMs: 800 },
    { id: 'shu-fu',   hp: 1150, speedPxPerSec: 35, size: 164, attackACooldownMs: 2000, attackBCooldownMs: 3200, phase2HpThreshold: 460,  attackBWarningMs: 1000 },
    { id: 'ji-yuan',  hp: 1100, speedPxPerSec: 45, size: 158, attackACooldownMs: 2000, attackBCooldownMs: 3200, phase2HpThreshold: 440,  attackBWarningMs: 1000 },
    { id: 'xing-yu',  hp: 1280, speedPxPerSec: 30, size: 176, attackACooldownMs: 2000, attackBCooldownMs: 3200, phase2HpThreshold: 512,  attackBWarningMs: 1000 },
    { id: 'zi-fu',    hp: 1200, speedPxPerSec: 38, size: 160, attackACooldownMs: 2000, attackBCooldownMs: 3200, phase2HpThreshold: 480,  attackBWarningMs: 1000 },
    { id: 'shi-zhong',hp: 2600, speedPxPerSec: 35, size: 184, attackACooldownMs: 1050, attackBCooldownMs: 2900, phase2HpThreshold: 1300, phaseThresholds: [0.6675, 0.3337], attackBWarningMs: 600 },
    { id: 'tian-ming',hp: 3800, speedPxPerSec: 30, size: 200, attackACooldownMs: 950,  attackBCooldownMs: 2600, phase2HpThreshold: 1900, phaseThresholds: [0.75, 0.5, 0.25], attackBWarningMs: 600 },
  ],
};

const DEFAULT_ENEMIES: EnemiesConfig = {
  version: 1,
  types: [
    // 2026-06-15 boss「小兵一下就死、感覺不出武器威力」: HP 拉到與子彈傷害同量級
    // (basic 2 下 / tank 多下 / 升級後 1 下)。鏡像 public/data/balance/enemies.json。
    { id: 'basic',  hp: 12, speedVx: -150, collisionDamage: 10, score: 1, fire_interval_ms: 2000 },
    { id: 'fast',   hp: 6,  speedVx: -220, collisionDamage: 10, score: 2, fire_interval_ms: 1000 },
    { id: 'tank',   hp: 20, speedVx: -80,  collisionDamage: 15, score: 3, fire_interval_ms: 4000 },
    { id: 'minion', hp: 4,  speedVx: -150, collisionDamage: 10, score: 0, fire_interval_ms: 2000 },
  ],
};

const DEFAULT_SPAWNING: SpawningConfig = {
  version: 1,
  // 2026-05-30 battle-juice: boss 嫌戰場太空 → 波數 3→5、每波量翻倍、生成更快。
  // 2026-05-31 enemy-diversity: boss 回饋「敵人至少多 2 倍」→ 每波量再 ×2。
  // 2026-05-31 boss-reachability (boss 回饋「魔王太爛、要測試太久、不一定看得到」):
  //   清 220 隻才出關底 boss 太冗長。配合「敵人加血(basic/fast 2、tank 6)+
  //   每 3 波小魔王壓陣」,把 wave_count 5→3、wave_sizes [20,32,44,56,68]→[8,12,16]
  //   (220→36 隻),目標「幾波就見到 boss、約 1 分鐘」。耐打度↑ 抵銷數量↓,
  //   單波仍熱鬧但整體節奏緊湊。wave_sizes.length 必須等於 wave_count。
  // 2026-06-13 boss「每個關卡太短」:波數 3→5 拉長單關;峰值單波量不變(維持 26,
  //   避免無物件池掉幀)——『多兩波』非『每波更多』。須與 spawning.json 同步。
  wave_count: 5,
  wave_sizes: [12, 16, 20, 24, 26],  // 5 波、峰值仍 26;length 必須 = wave_count
  spawn_interval_ms: 450,    // 700→450 生成更密、戰場更滿(老闆「攻擊頻率太低」)
  wave_gap_ms: 2500,       // 3000→2500 波間空檔縮短,節奏更緊湊
};

const DEFAULT_PLAYER: PlayerConfig = {
  version: 1,
  initial_hp: 100,
  move_speed: 280,
  bullet_vx: 500,
  // 2026-06-06 老闆「所有攻擊力應降低,進階才有效果」:base 10→6(升級/進化提升相對更有感)。
  bullet_damage: 6,
  // 2026-06-06 老闆「發射速度應減半」:160→320(≈3.1 shots/sec)。降基礎輸出,讓連射/快充升級有感。
  fire_interval_ms: 320,
};

// weapon-evolution: 場內武器進化「進化後數值」(R-004 包 C). 配方門檻在
// src/game/systems/weapon-evolution-recipes.json;這裡只放生效後加進當局 RunModifiers
// 的質變數值. 必與 public/data/balance/weapon-evolution.json 完全同步.
// pierce/lane-damage-falloff (boss 2026-06-14「不能打得又廣傷害又高」): 廣命中後段傷害遞減
// 旋鈕,必與 public/data/balance/weapon-evolution.json 同步。piercing-bullet 的單發加成從
// 8→4(穿透已給「廣」的好處,不該同時又給高單發傷)。
const DEFAULT_WEAPON_EVOLUTION: WeaponEvolutionConfig = {
  version: 1,
  pierceDamageFalloff: 0.55,
  laneDamageFalloff: 0.6,
  evolutions: [
    { id: 'piercing-bullet', bulletDamageBonus: 4, pierceCount: 2, bounceBonus: 0, spreadBonus: 0, spreadBlastRadius: 0 },
    { id: 'blast-spread',    bulletDamageBonus: 4, pierceCount: 0, bounceBonus: 0, spreadBonus: 2, spreadBlastRadius: 36 },
    { id: 'bouncing-storm',  bulletDamageBonus: 4, pierceCount: 0, bounceBonus: 3, spreadBonus: 0, spreadBlastRadius: 0 },
  ],
};

export const DEFAULT_BALANCE: BalanceConfig = {
  bosses: DEFAULT_BOSSES,
  enemies: DEFAULT_ENEMIES,
  spawning: DEFAULT_SPAWNING,
  player: DEFAULT_PLAYER,
  weaponEvolution: DEFAULT_WEAPON_EVOLUTION,
};

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function fetchJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[balance-loader] ${url} returned ${res.status}, using fallback`);
      return fallback;
    }
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[balance-loader] failed to fetch ${url}:`, err, '— using fallback');
    return fallback;
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

let _cached: BalanceConfig | null = null;

/**
 * Load all balance configs from public/data/balance/*.json.
 * Results are cached after the first call; subsequent calls return the same object.
 * On any network or parse failure, the corresponding file falls back to its
 * hardcoded default — the game will never white-screen due to a missing balance file.
 */
export async function loadBalance(): Promise<BalanceConfig> {
  if (_cached) return _cached;

  const [bosses, enemies, spawning, player, weaponEvolution] = await Promise.all([
    fetchJson<BossesConfig>('data/balance/bosses.json', DEFAULT_BOSSES),
    fetchJson<EnemiesConfig>('data/balance/enemies.json', DEFAULT_ENEMIES),
    fetchJson<SpawningConfig>('data/balance/spawning.json', DEFAULT_SPAWNING),
    fetchJson<PlayerConfig>('data/balance/player.json', DEFAULT_PLAYER),
    fetchJson<WeaponEvolutionConfig>('data/balance/weapon-evolution.json', DEFAULT_WEAPON_EVOLUTION),
  ]);

  _cached = { bosses, enemies, spawning, player, weaponEvolution };
  return _cached;
}

/**
 * Synchronously return the cached BalanceConfig, or DEFAULT_BALANCE if not yet loaded.
 * Always succeeds — never throws.
 */
export function getBalanceSync(): BalanceConfig {
  return _cached ?? DEFAULT_BALANCE;
}

/**
 * Reset the internal cache. Useful in tests to force a fresh fetch.
 */
export function resetBalanceCache(): void {
  _cached = null;
}

// ── Boss pool patching ────────────────────────────────────────────────────────

import type { BossDef } from '../entities/boss';

/**
 * Apply boss balance overrides from a loaded BalanceConfig to the live BossDef
 * objects in the provided pool arrays.  Mutates entries in-place so that Boss
 * instances created after this call use JSON-configured stats.
 *
 * Caller (LoadingScene) passes all pool arrays to avoid circular imports.
 */
export function applyBossBalance(config: BalanceConfig, allDefs: BossDef[]): void {
  for (const def of allDefs) {
    const override = config.bosses.bosses.find((b) => b.id === def.id);
    if (!override) continue;
    def.hp                = override.hp;
    def.speedPxPerSec     = override.speedPxPerSec;
    def.size              = override.size;
    def.attackACooldownMs = override.attackACooldownMs;
    def.attackBCooldownMs = override.attackBCooldownMs;
    def.phase2HpThreshold = override.phase2HpThreshold;
    def.attackBWarningMs  = override.attackBWarningMs;
    if (override.phaseThresholds) {
      def.phaseThresholds = override.phaseThresholds;
    }
  }
}
